import { componentSchemas } from '@smoothie-framework/ui-core/schemas';
import { classifyBorderStyleUtilityValue, classifyBorderUtilityValue, classifyBorderWidthUtilityValue, classifyDimensionUtilityValue, classifyRadiusUtilityValue, classifyShadowUtilityValue, classifySpacingUtilityValue, classifyZIndexUtilityValue, getBreakpointOrder, isBreakpointKey, isResponsiveConditionKey, isUtilityInteractionStateProp, isUtilityPropName, resolveHideBelowCondition, resolveResponsiveCondition, resolveUtilityPropName, } from '@smoothie-framework/ui-styles/styling';
import { extensionIndex } from '../authoring/extension-registry';
import { GENERIC_RUNTIME_PASSTHROUGH_PROP_NAMES, isRuntimeHostAllowedPropName, isSupplementalRuntimeAllowedPropName, } from '../schema/runtime-prop-allowances';
import { schemaComponentIdForRuntimeComponent } from '../schema/component-aliases';
import { getRuntimeSchemaPropGuardrail } from '../schema/runtime-prop-guardrails';
import { isReactiveAccessor, readReactiveAccessor } from '../state/accessor';
const schemaPropsByComponent = new Map();
const componentSchemaLookup = componentSchemas;
function canonicalComponentType(componentType) {
    return schemaComponentIdForRuntimeComponent(componentType);
}
function reportSchemaIssue(mode, message) {
    if (mode === 'throw') {
        throw new Error(message);
    }
    globalThis.console.warn(message);
}
function toKebabCase(value) {
    return value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}
function readProp(props, propName) {
    if (propName in props) {
        return { exists: true, value: props[propName] };
    }
    const kebabName = toKebabCase(propName);
    if (kebabName in props) {
        return { exists: true, value: props[kebabName] };
    }
    for (const [candidateName, candidateValue] of Object.entries(props)) {
        if (isUtilityPropName(candidateName) && resolveUtilityPropName(candidateName) === propName) {
            return { exists: true, value: candidateValue };
        }
    }
    return { exists: false, value: undefined };
}
function getSchemaPropMap(componentType) {
    const canonicalType = canonicalComponentType(componentType);
    const cached = schemaPropsByComponent.get(canonicalType);
    if (cached) {
        return cached;
    }
    const schema = componentSchemaLookup[canonicalType];
    if (!schema?.props) {
        return undefined;
    }
    schemaPropsByComponent.set(canonicalType, schema.props);
    return schema.props;
}
function isDataOrAriaPropName(propName) {
    return propName.startsWith('data-') || propName.startsWith('aria-');
}
function isSchemaDeclaredPropName(schemaProps, propName) {
    if (propName in schemaProps) {
        return true;
    }
    const canonicalUtilityPropName = isUtilityPropName(propName)
        ? resolveUtilityPropName(propName)
        : propName;
    if (canonicalUtilityPropName in schemaProps) {
        return true;
    }
    for (const schemaPropName of Object.keys(schemaProps)) {
        if (toKebabCase(schemaPropName) === propName) {
            return true;
        }
    }
    return false;
}
function isSupplementalAllowedPropName(componentType, propName) {
    return isSupplementalRuntimeAllowedPropName(componentType, propName);
}
function isAllowedUnknownPropName(componentType, propName, schemaProps) {
    return isSchemaDeclaredPropName(schemaProps, propName)
        || GENERIC_RUNTIME_PASSTHROUGH_PROP_NAMES.has(propName)
        || isDataOrAriaPropName(propName)
        || isSupplementalAllowedPropName(componentType, propName);
}
function getSchemaPropDef(schemaProps, propName) {
    if (propName in schemaProps) {
        return schemaProps[propName];
    }
    const canonicalPropName = isUtilityPropName(propName)
        ? resolveUtilityPropName(propName)
        : propName;
    if (canonicalPropName in schemaProps) {
        return schemaProps[canonicalPropName];
    }
    for (const [schemaPropName, schemaPropDef] of Object.entries(schemaProps)) {
        if (toKebabCase(schemaPropName) === propName) {
            return schemaPropDef;
        }
    }
    return undefined;
}
function validatePseudoUtilityProp(componentType, propName, value, schemaProps, mode, theme) {
    if (!isUtilityInteractionStateProp(propName)) {
        return;
    }
    if (!isPlainObject(value)) {
        reportInvalidType(componentType, propName, 'object', value, mode);
        return;
    }
    for (const [nestedPropName, nestedValue] of Object.entries(value)) {
        const canonicalNestedPropName = isUtilityPropName(nestedPropName)
            ? resolveUtilityPropName(nestedPropName)
            : nestedPropName;
        if (!isUtilityPropName(nestedPropName) && !isUtilityPropName(canonicalNestedPropName)) {
            reportSchemaIssue(mode, `[smoothie/ui] Unknown pseudo utility prop for ${componentType}.${propName}.${nestedPropName}: prop is not a supported utility prop`);
            continue;
        }
        const nestedDef = getSchemaPropDef(schemaProps, canonicalNestedPropName);
        if (!nestedDef) {
            reportSchemaIssue(mode, `[smoothie/ui] Unknown pseudo utility prop for ${componentType}.${propName}.${nestedPropName}: prop is not declared in schema`);
            continue;
        }
        validateValueAgainstDefinition(componentType, `${propName}.${nestedPropName}`, nestedDef, nestedValue, mode, canonicalNestedPropName, theme);
    }
}
function isMissingRequiredProp(value, exists) {
    return (!exists ||
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim().length === 0));
}
function isNumberLike(value) {
    return (typeof value === 'number' ||
        (typeof value === 'string' && value.trim().length > 0 && !Number.isNaN(Number(value))));
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function reportInvalidType(componentType, propPath, expected, value, mode) {
    const message = `[smoothie/ui] Invalid prop type for ${componentType}.${propPath}: expected ${expected}, got ${typeof value}`;
    reportSchemaIssue(mode, message);
}
function normalizePropPathSegments(propPath) {
    return propPath
        .split('.')
        .map((segment) => segment.replace(/\[\d+\]$/, ''))
        .filter((segment) => !isResponsiveConditionKey(segment))
        .filter((segment) => segment.length > 0);
}
function spacingUtilityPropNameForPath(propPath) {
    const segments = normalizePropPathSegments(propPath);
    const leaf = segments.at(-1) ?? propPath;
    const parent = segments.at(-2);
    if ((parent === 'padding' || parent === 'margin') && (leaf === 'x' || leaf === 'y')) {
        return `${parent}${leaf.toUpperCase()}`;
    }
    return leaf;
}
function validateValueAgainstDefinition(componentType, propPath, def, value, mode, lookupPropPath = propPath, theme) {
    if (!def.type || value === undefined) {
        return;
    }
    if (def.accessor === true && typeof value === 'function') {
        const resolvedValue = value();
        if (def.required && resolvedValue === undefined) {
            reportSchemaIssue(mode, `[smoothie/ui] Missing required prop for ${componentType}.${propPath}`);
            return;
        }
        validateValueAgainstDefinition(componentType, propPath, def, resolvedValue, mode, lookupPropPath, theme);
        return;
    }
    if (isReactiveAccessor(value)) {
        validateValueAgainstDefinition(componentType, propPath, def, readReactiveAccessor(value), mode, lookupPropPath, theme);
        return;
    }
    if (componentType === 'Select' && lookupPropPath === 'options' && typeof value === 'function') {
        return;
    }
    if (lookupPropPath === 'hideFrom' || lookupPropPath === 'hideBelow') {
        if (typeof value !== 'string') {
            reportInvalidType(componentType, propPath, 'string', value, mode);
            return;
        }
        const isValidBreakpoint = isBreakpointKey(value, theme);
        const conditionName = lookupPropPath === 'hideBelow' ? resolveHideBelowCondition(value, theme) : value;
        if (!isValidBreakpoint || !conditionName || !resolveResponsiveCondition(conditionName, theme)?.mediaQuery) {
            reportSchemaIssue(mode, `[smoothie/ui] Invalid breakpoint key for ${componentType}.${propPath}: ${JSON.stringify(value)}`);
        }
        return;
    }
    if (!def.responsive && isPlainObject(value) && Object.keys(value).some((key) => isResponsiveConditionKey(key, theme))) {
        reportSchemaIssue(mode, `[smoothie/ui] Responsive values are not supported for ${componentType}.${propPath}`);
        return;
    }
    if (def.responsive && isPlainObject(value)) {
        const keys = Object.keys(value);
        const hasBreakpointKeys = keys.some((key) => isResponsiveConditionKey(key, theme));
        if (hasBreakpointKeys) {
            for (const [breakpoint, responsiveValue] of Object.entries(value)) {
                if (!isResponsiveConditionKey(breakpoint, theme)) {
                    reportSchemaIssue(mode, `[smoothie/ui] Invalid responsive condition key for ${componentType}.${propPath}: ${JSON.stringify(breakpoint)}`);
                    continue;
                }
                validateValueAgainstDefinition(componentType, `${propPath}.${breakpoint}`, { ...def, responsive: false }, responsiveValue, mode, lookupPropPath, theme);
            }
            return;
        }
    }
    if (def.responsive && Array.isArray(value)) {
        const breakpointOrder = getBreakpointOrder(theme);
        if (value.length > breakpointOrder.length) {
            reportSchemaIssue(mode, `[smoothie/ui] Invalid responsive array for ${componentType}.${propPath}: expected at most ${breakpointOrder.length} entries, got ${value.length}`);
        }
        for (const [index, responsiveValue] of value.entries()) {
            if (!(index in value) || index >= breakpointOrder.length) {
                continue;
            }
            const breakpoint = breakpointOrder[index];
            validateValueAgainstDefinition(componentType, `${propPath}.${breakpoint}`, { ...def, responsive: false }, responsiveValue, mode, lookupPropPath, theme);
        }
        return;
    }
    if (def.type === 'enum') {
        const allowedValues = new Set(def.values ?? []);
        const extensionVariants = extensionIndex.mergeRecipeExtensions(componentType).variants[lookupPropPath];
        const runtimePropGuardrail = getRuntimeSchemaPropGuardrail(componentType, lookupPropPath);
        for (const extensionValue of Object.keys(extensionVariants ?? {})) {
            allowedValues.add(extensionValue);
        }
        if (typeof value !== 'string' && typeof value !== 'number') {
            const message = `[smoothie/ui] Invalid prop type for ${componentType}.${propPath}: expected enum value (${(def.values ?? []).join(', ')}), got ${typeof value}`;
            reportSchemaIssue(mode, message);
            return;
        }
        if (allowedValues.size > 0 && !allowedValues.has(value)) {
            if (typeof value === 'string' && runtimePropGuardrail?.allowRawStringValue === true) {
                return;
            }
            const message = `[smoothie/ui] Invalid enum value for ${componentType}.${propPath}: ${JSON.stringify(value)}. Allowed: ${Array.from(allowedValues).map((entry) => JSON.stringify(entry)).join(', ')}`;
            reportSchemaIssue(mode, message);
        }
        return;
    }
    if (def.type === 'string') {
        if (typeof value !== 'string') {
            reportInvalidType(componentType, propPath, 'string', value, mode);
        }
        return;
    }
    if (def.type === 'number') {
        if (!isNumberLike(value)) {
            reportInvalidType(componentType, propPath, 'number', value, mode);
        }
        return;
    }
    if (def.type === 'string_or_number') {
        if (typeof value !== 'string' && !isNumberLike(value)) {
            reportInvalidType(componentType, propPath, 'string or number', value, mode);
        }
        return;
    }
    if (def.type === 'string_or_string_array') {
        if (typeof value === 'string') {
            return;
        }
        if (!Array.isArray(value)) {
            reportInvalidType(componentType, propPath, 'string or string[]', value, mode);
            return;
        }
        for (const [index, item] of value.entries()) {
            if (typeof item !== 'string') {
                reportInvalidType(componentType, `${propPath}[${index}]`, 'string', item, mode);
            }
        }
        return;
    }
    if (def.type === 'bool') {
        const isBoolString = typeof value === 'string' && (value === 'true' || value === 'false');
        if (typeof value !== 'boolean' && !isBoolString) {
            reportInvalidType(componentType, propPath, 'bool', value, mode);
        }
        return;
    }
    if (def.type === 'spacing_token') {
        if (def.properties && isPlainObject(value)) {
            const objectValue = value;
            for (const [nestedPropName, nestedDef] of Object.entries(def.properties)) {
                const { exists, value: nestedValue } = readProp(objectValue, nestedPropName);
                if (nestedDef.required && isMissingRequiredProp(nestedValue, exists)) {
                    const message = `[smoothie/ui] Missing required prop for ${componentType}.${propPath}.${nestedPropName}`;
                    reportSchemaIssue(mode, message);
                    continue;
                }
                if (!exists) {
                    continue;
                }
                validateValueAgainstDefinition(componentType, `${propPath}.${nestedPropName}`, nestedDef, nestedValue, mode, `${lookupPropPath}.${nestedPropName}`, theme);
            }
            return;
        }
        const spacingPropName = spacingUtilityPropNameForPath(lookupPropPath);
        const classifiedValue = classifySpacingUtilityValue(spacingPropName, value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, 'spacing_token', value, mode);
        }
        return;
    }
    if (def.type === 'border_token') {
        if (typeof value !== 'string') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyBorderUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'border_width_token') {
        if (typeof value !== 'string' && typeof value !== 'number') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyBorderWidthUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'border_style_token') {
        if (typeof value !== 'string') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyBorderStyleUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'size_token') {
        if (typeof value !== 'string' && typeof value !== 'number') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyDimensionUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'radius_token') {
        if (typeof value !== 'string' && typeof value !== 'number') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyRadiusUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'shadow_token') {
        if (typeof value !== 'string') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyShadowUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'z_index_token') {
        if (typeof value !== 'string' && typeof value !== 'number') {
            reportInvalidType(componentType, propPath, def.type, value, mode);
            return;
        }
        const classifiedValue = classifyZIndexUtilityValue(value);
        if (!classifiedValue.valid) {
            reportInvalidType(componentType, propPath, def.type, value, mode);
        }
        return;
    }
    if (def.type === 'content') {
        if (!isValidRuntimeContentValue(value)) {
            reportInvalidType(componentType, propPath, 'content', value, mode);
        }
        return;
    }
    if (def.type === 'color_token') {
        if (typeof value !== 'string') {
            reportInvalidType(componentType, propPath, 'color_token', value, mode);
        }
        return;
    }
    if (def.type === 'object') {
        if (!isPlainObject(value)) {
            reportInvalidType(componentType, propPath, 'object', value, mode);
            return;
        }
        const objectValue = value;
        for (const [nestedPropName, nestedDef] of Object.entries(def.properties ?? {})) {
            const { exists, value: nestedValue } = readProp(objectValue, nestedPropName);
            if (nestedDef.required && isMissingRequiredProp(nestedValue, exists)) {
                const message = `[smoothie/ui] Missing required prop for ${componentType}.${propPath}.${nestedPropName}`;
                reportSchemaIssue(mode, message);
                continue;
            }
            if (!exists) {
                continue;
            }
            validateValueAgainstDefinition(componentType, `${propPath}.${nestedPropName}`, nestedDef, nestedValue, mode, `${lookupPropPath}.${nestedPropName}`, theme);
        }
        return;
    }
    if (def.type === 'array') {
        if (!Array.isArray(value)) {
            reportInvalidType(componentType, propPath, 'array', value, mode);
            return;
        }
        if (!def.items) {
            return;
        }
        for (const [index, item] of value.entries()) {
            if (item === undefined) {
                const message = `[smoothie/ui] Invalid prop type for ${componentType}.${propPath}[${index}]: expected defined array item, got undefined`;
                reportSchemaIssue(mode, message);
                continue;
            }
            validateValueAgainstDefinition(componentType, `${propPath}[${index}]`, def.items, item, mode, `${lookupPropPath}[${index}]`, theme);
        }
    }
}
function isValidRuntimeContentValue(value) {
    if (Array.isArray(value)) {
        return value.every((entry) => isValidRuntimeContentValue(entry));
    }
    return value === undefined
        || value === null
        || typeof value === 'boolean'
        || typeof value === 'string'
        || typeof value === 'number'
        || typeof value === 'function'
        || isRuntimeUINodeValue(value)
        || isSpecNodeValue(value);
}
function isRuntimeUINodeValue(value) {
    return isPlainObject(value)
        && typeof value.type === 'string'
        && isPlainObject(value.props)
        && Array.isArray(value.children);
}
function isSpecNodeValue(value) {
    return isPlainObject(value)
        && typeof value.component === 'string'
        && (value.props === undefined || isPlainObject(value.props))
        && (value.slots === undefined || isPlainObject(value.slots));
}
export function validateSchemaProps(componentType, props, mode, theme) {
    validateSchemaPropsAgainstComponentTypes([componentType], props, mode, theme);
}
export function validateUINodeSchema(node, mode, theme) {
    const runtimeNode = node;
    validateSchemaPropsAgainstComponentTypes([node.type, ...(runtimeNode._runtimeComposedSchemaTypes ?? [])], node.props, mode, theme);
}
function validateSchemaPropsAgainstComponentTypes(componentTypes, props, mode, theme) {
    if (!props)
        return;
    const schemaEntries = Array.from(new Set(componentTypes))
        .map((componentType) => ({ componentType, schemaProps: getSchemaPropMap(componentType) }))
        .filter((entry) => Boolean(entry.schemaProps));
    if (schemaEntries.length === 0) {
        return;
    }
    for (const { componentType, schemaProps } of schemaEntries) {
        for (const [propName, def] of Object.entries(schemaProps)) {
            if (!def?.required)
                continue;
            const { exists, value } = readProp(props, propName);
            const missing = isMissingRequiredProp(value, exists);
            if (missing) {
                const message = `[smoothie/ui] Missing required prop for ${componentType}.${propName}`;
                reportSchemaIssue(mode, message);
            }
        }
    }
    for (const propName of Object.keys(props)) {
        if (isRuntimeHostAllowedPropName(componentTypes[0], propName)
            || schemaEntries.some(({ componentType, schemaProps }) => isAllowedUnknownPropName(componentType, propName, schemaProps))) {
            continue;
        }
        reportSchemaIssue(mode, `[smoothie/ui] Unknown prop for ${componentTypes[0]}.${propName}: prop is not declared in schema`);
    }
    for (const { componentType, schemaProps } of schemaEntries) {
        for (const [propName, def] of Object.entries(schemaProps)) {
            const { exists, value } = readProp(props, propName);
            if (!exists)
                continue;
            validateValueAgainstDefinition(componentType, propName, def, value, mode, propName, theme);
        }
    }
    for (const { componentType, schemaProps } of schemaEntries) {
        for (const [propName, value] of Object.entries(props)) {
            validatePseudoUtilityProp(componentType, propName, value, schemaProps, mode, theme);
        }
    }
}
//# sourceMappingURL=schema-validation.js.map